import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './SyncStatus.css';

const API = '';

function formatSyncType(type) {
  const labels = {
    bukku_invoices: 'RMC Bukku',
    bukku_wf_invoices: 'W Factory Bukku',
  };
  return labels[type] || type;
}

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-MY', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-MY', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
  });
}

function formatDuration(startStr, endStr) {
  if (!startStr || !endStr) return '—';
  const ms = new Date(endStr) - new Date(startStr);
  if (ms < 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  return `${mins}m ${secs}s`;
}

export default function SyncStatus() {
  const { isSuperadmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState({});
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/sync/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch sync status');
      const json = await res.json();
      setData(json);
      setError(null);

      // Check if any syncs are still running
      const hasRunning = json.history.some(h => h.status === 'running');
      if (!hasRunning && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setSyncing({});
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchStatus]);

  const triggerSync = async (type) => {
    setSyncing(prev => ({ ...prev, [type]: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/sync/trigger`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ type })
      });
      if (!res.ok) throw new Error('Failed to trigger sync');

      // Start polling every 5 seconds
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(fetchStatus, 5000);
      // Also fetch immediately after a short delay
      setTimeout(fetchStatus, 1000);
    } catch (err) {
      setSyncing(prev => ({ ...prev, [type]: false }));
      alert('Sync trigger failed: ' + err.message);
    }
  };

  // Derived data
  const getLastSync = (syncType) => {
    if (!data) return null;
    return data.lastSuccessful.find(s => s.sync_type === syncType);
  };

  const getInvoiceCount = (company) => {
    if (!data) return 0;
    const match = data.invoiceCounts.find(c => c.company === company);
    return match ? parseInt(match.count, 10) : 0;
  };

  const rmcSync = getLastSync('bukku_invoices');
  const wfSync = getLastSync('bukku_wf_invoices');
  const rmcCount = getInvoiceCount('rmc');
  const wfCount = getInvoiceCount('wfactory');
  const hasAnyRunning = data?.history.some(h => h.status === 'running');

  if (loading) {
    return (
      <div className="sync-status-page">
        <div className="sync-loading">
          <div className="loading-spinner"></div>
          <p>Loading sync status...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="sync-status-page">
        <div className="sync-error">
          <p>Error: {error}</p>
          <button onClick={fetchStatus} className="sync-btn all" style={{ marginTop: 12 }}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="sync-status-page">
      <h2>Sync Status</h2>

      {/* Health Summary Cards */}
      <div className="sync-health-grid">
        <div className="sync-health-card rmc">
          <div className="sync-health-header">
            <span className="sync-health-title">RMC Bukku</span>
            <span className={`sync-status-dot ${rmcSync ? 'green' : 'gray'}`}></span>
          </div>
          <div className="sync-health-detail">Last sync: <span>{rmcSync ? timeAgo(rmcSync.sync_started_at) : 'Never'}</span></div>
          <div className="sync-health-detail">Records synced: <span>{rmcSync ? (rmcSync.records_synced || 0).toLocaleString() : '—'}</span></div>
        </div>

        <div className="sync-health-card wfactory">
          <div className="sync-health-header">
            <span className="sync-health-title">W Factory Bukku</span>
            <span className={`sync-status-dot ${wfSync ? 'green' : 'gray'}`}></span>
          </div>
          <div className="sync-health-detail">Last sync: <span>{wfSync ? timeAgo(wfSync.sync_started_at) : 'Never'}</span></div>
          <div className="sync-health-detail">Records synced: <span>{wfSync ? (wfSync.records_synced || 0).toLocaleString() : '—'}</span></div>
        </div>

        <div className="sync-health-card database">
          <div className="sync-health-header">
            <span className="sync-health-title">Database</span>
            <span className="sync-status-dot green"></span>
          </div>
          <div className="sync-health-detail">RMC invoices: <span>{rmcCount.toLocaleString()}</span></div>
          <div className="sync-health-detail">W Factory invoices: <span>{wfCount.toLocaleString()}</span></div>
          <div className="sync-health-value small" style={{ marginTop: 4 }}>{(rmcCount + wfCount).toLocaleString()} total</div>
        </div>
      </div>

      {/* Manual Sync Buttons — superadmin only; admin views status read-only */}
      {isSuperadmin && (
        <div className="sync-actions">
          <button
            className="sync-btn rmc"
            onClick={() => triggerSync('rmc')}
            disabled={syncing.rmc || syncing.all || hasAnyRunning}
          >
            {syncing.rmc ? 'Syncing...' : 'Sync RMC'}
          </button>
          <button
            className="sync-btn wfactory"
            onClick={() => triggerSync('wfactory')}
            disabled={syncing.wfactory || syncing.all || hasAnyRunning}
          >
            {syncing.wfactory ? 'Syncing...' : 'Sync W Factory'}
          </button>
          <button
            className="sync-btn all"
            onClick={() => triggerSync('all')}
            disabled={syncing.rmc || syncing.wfactory || syncing.all || hasAnyRunning}
          >
            {syncing.all ? 'Syncing...' : 'Sync All'}
          </button>
        </div>
      )}

      {/* Sync History Table */}
      <div className="sync-history-card">
        <h3>Sync History</h3>
        <div className="report-table-scroll" role="region" aria-label="Report table — scroll to view all columns" tabIndex={0}><table className="sync-history-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Started</th>
              <th>Completed</th>
              <th>Duration</th>
              <th>Records</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.history.length === 0 ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', color: 'var(--rmc-ink3)' }}>No sync history yet</td></tr>
            ) : (
              data.history.map((row) => (
                <tr key={row.id}>
                  <td><span className="sync-type-label">{formatSyncType(row.sync_type)}</span></td>
                  <td>{formatTime(row.sync_started_at)}</td>
                  <td>{row.sync_completed_at ? formatTime(row.sync_completed_at) : '—'}</td>
                  <td>{formatDuration(row.sync_started_at, row.sync_completed_at)}</td>
                  <td>{row.records_synced != null ? row.records_synced.toLocaleString() : '—'}</td>
                  <td>
                    <span className={`sync-badge ${row.status}`}>
                      {row.status === 'completed' ? 'Completed' : row.status === 'failed' ? 'Failed' : 'Running'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}
